/* eslint-disable @typescript-eslint/no-invalid-void-type */
import { apiSlice } from './apiSlice';
import VehicleStatus from '../../core/enum/VehicleStatus';
import Vehicle from '../../core/models/Vehicle';
import VehicleAuctionHistory from '../../core/models/VehicleAuctionHistory';

export const vehicleApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getVehicles: builder.query<Vehicle[], void>({
      query: () => '/vehicles',
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Vehicle' as const, id })),
              { type: 'Vehicle', id: 'LIST' },
            ]
          : [{ type: 'Vehicle', id: 'LIST' }],
    }),
    getVehicle: builder.query<Vehicle, number>({
      query: (id) => `/vehicles/${id}`,
      providesTags: (result, error, id) => [{ type: 'Vehicle', id }],
    }),
    getVehicleAuctionHistory: builder.query<VehicleAuctionHistory[], string>({
      query: (id) => `/vehicles/auctions/${id}`,
      providesTags: (result, error, id) => [{ type: 'VehicleAuctionHistory', id }],
    }),
    addVehicle: builder.mutation<Vehicle, Vehicle>({
      query: (data) => ({
        url: '/vehicles/admin',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Vehicle', id: 'LIST' }],
    }),
    updateVehicle: builder.mutation<Vehicle, Vehicle>({
      query: (data) => ({
        url: '/vehicles',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Vehicle', id }],
    }),
    updateVehicleStatus: builder.mutation<Vehicle, { id: string; status: VehicleStatus }>({
      query: (args) => ({
        url: '/vehicles/updateStatus',
        method: 'PUT',
        body: { id: args.id, status: args.status },
      }),
      invalidatesTags: ['Vehicle'],
    }),
    deleteVehicle: builder.mutation<void, string>({
      query: (id) => ({
        url: `/vehicles/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Vehicle', id }],
    }),
    getBuyerVehicles: builder.query<Vehicle[], string>({
      query: (id) => `/vehicles/buyer/${id}`,
      providesTags: (result, error, id) => [{ type: 'buyerVehicles', id }],
    }),
    getSellerVehicles: builder.query<Vehicle[], string>({
      query: (id) => `/vehicles/seller/${id}`,
      providesTags: (result, error, id) => [{ type: 'sellerVehicles', id }],
    }),
    deleteImage: builder.mutation<void, { vehicleId: string; imageId: string }>({
      query: (args) => ({
        url: `/vehicles/${args.vehicleId}/images/${args.imageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Vehicle'],
    }),
    sortImages: builder.mutation<void, Vehicle>({
      query: (data) => ({
        url: `/vehicles/images/sortOrder/${data.id}`,
        method: 'PUT',
        body: data.images.map((image, index) => ({
          id: image.id,
          filePath: image.filePath,
          fileName: image.fileName,
          sortOrder: index,
        })),
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Vehicle', id }],
    }),
  }),
});

export const {
  useGetVehiclesQuery,
  useGetVehicleQuery,
  useGetVehicleAuctionHistoryQuery,
  useAddVehicleMutation,
  useUpdateVehicleMutation,
  useUpdateVehicleStatusMutation,
  useDeleteVehicleMutation,
  useGetBuyerVehiclesQuery,
  useGetSellerVehiclesQuery,
  useDeleteImageMutation,
  useSortImagesMutation,
} = vehicleApiSlice;
